"use client"

import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react"
import {
  fetchAddressesForCustomerAction,
  updateInvoiceToAddressAction,
  updateShipToAddressAction,
} from "@/actions/basketActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { CircleAlertIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { countryForCountryCodeMap, getInitialAddress } from "@/lib/helper"
import { BasketData } from "@/lib/intershop/interfaces/basket/basket"
import { getCountry } from "@/lib/locale/locale-common-utils"
import { ExtendedAddressRO } from "@/lib/storefront/addresses/address"
import { CheckoutContext } from "@/lib/storefront/checkout/checkout-service"
import AddAddressButton from "@/components/common/addresses/AddAddressButton"
import AddressSelectSheet from "@/components/common/addresses/AddressSelectSheet"
import EditOrCreateAddress from "@/components/common/addresses/EditOrCreateAddress"
import SelectAddress from "@/components/common/addresses/SelectAddress"
import FallbackCard from "@/components/common/FallbackCard"
import { UbCheckoutAddressesViewStoryblok } from "@/components/storyblok/component-types"

interface CheckoutAddressesProps {
  blok: UbCheckoutAddressesViewStoryblok
  checkoutContext: CheckoutContext
}

function MissingAddressNotice({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={"flex min-h-32 flex-row gap-3 rounded-sm border border-primary/30 bg-white p-4 text-sm text-gray-700"}
      role="alert"
    >
      <CircleAlertIcon className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
      <p>{children}</p>
    </div>
  )
}

export default function CheckoutAddressesClient({ blok, checkoutContext }: CheckoutAddressesProps) {
  const t = useTranslations("storefront")

  const { basketData, setBasketData, addresses, setAddresses } = useUbStorefrontState()

  const [updateShipToAddress, setUpdateShipToAddress] = useState<boolean>(false)
  const [updateInvoiceAddress, setUpdateInvoiceAddress] = useState<boolean>(false)

  const [initial, setInitial] = useState<boolean>(true)

  const invoiceToAddressesInit = useMemo(() => {
    if (addresses) {
      return addresses.filter((address) => address.invoiceToAddress)
    } else {
      return []
    }
  }, [addresses])

  const shipToAddressesInit = useMemo(() => {
    if (addresses) {
      return addresses.filter((address) => address.shipToAddress)
    } else {
      return []
    }
  }, [addresses])

  const preferredInvoiceToAddress = useMemo(() => {
    return checkoutContext.userData?.preferredInvoiceToAddress ?? invoiceToAddressesInit[0]
  }, [checkoutContext.userData?.preferredInvoiceToAddress, invoiceToAddressesInit])

  const preferredShipToAddress = useMemo(() => {
    return checkoutContext.userData?.preferredShipToAddress ?? shipToAddressesInit[0]
  }, [checkoutContext.userData?.preferredShipToAddress, shipToAddressesInit])

  const basketInvoiceToAddress = useMemo(() => {
    return addresses?.find((a) => {
      return a?.urn === basketData?.data?.invoiceToAddress
    })
  }, [addresses, basketData?.data?.invoiceToAddress])

  const basketShipToAddress = useMemo(() => {
    return addresses?.find((a) => {
      return a?.urn === basketData?.data?.commonShipToAddress
    })
  }, [addresses, basketData?.data?.commonShipToAddress])

  const invoiceToAddressesSorted = useMemo(() => {
    const sorted = invoiceToAddressesInit
      .sort((a, b) => (b?.id === preferredInvoiceToAddress?.id ? 1 : -1))
      .sort((a, b) => (b?.id === basketInvoiceToAddress?.id ? 1 : -1))
    //console.dir(sorted)
    return sorted
  }, [basketInvoiceToAddress, preferredInvoiceToAddress, invoiceToAddressesInit])

  const shipToAddressesSorted = useMemo(() => {
    const sorted = shipToAddressesInit
      .sort((a, b) => (b?.id === preferredShipToAddress?.id ? 1 : -1))
      .sort((a, b) => (b?.id === basketShipToAddress?.id ? 1 : -1))
    //console.dir(sorted)
    return sorted
  }, [basketShipToAddress, preferredShipToAddress, shipToAddressesInit])

  // Add local state for addresses and setters
  /*const [invoiceToAddresses, setInvoiceToAddresses] = useState<ExtendedAddressRO[]>(invoiceToAddressesSorted)
  const [shipToAddresses, setShipToAddresses] = useState<ExtendedAddressRO[]>(shipToAddressesSorted)*/

  const [selectedShipToAddress, setSelectedShipToAddress] = useState(basketShipToAddress ?? preferredShipToAddress)
  const [selectedInvoiceToAddress, setSelectedInvoiceToAddress] = useState(
    basketInvoiceToAddress ?? preferredInvoiceToAddress
  )

  function isSameAddress(addressA?: ExtendedAddressRO, addressB?: ExtendedAddressRO): boolean {
    if (!addressA || !addressB) {
      return false
    }

    return addressA.urn === addressB.urn || addressA.id === addressB.id
  }

  const isShipToSameAsInvoiceTo = useMemo(() => {
    return isSameAddress(selectedInvoiceToAddress, selectedShipToAddress)
  }, [selectedInvoiceToAddress, selectedShipToAddress])

  const syncCheckoutAddresses = useCallback(
    async (addressList: ExtendedAddressRO[], basketDataOverride?: BasketData) => {
      const effectiveBasketData = basketDataOverride ?? basketData
      const invoiceAddressFromBasket = addressList.find((address) => {
        return address.invoiceToAddress && address.urn === effectiveBasketData?.data?.invoiceToAddress
      })
      const nextInvoiceToAddress =
        invoiceAddressFromBasket ??
        addressList.find((address) => address.invoiceToAddress && address.preferredBillingAddress === "true") ??
        addressList.find((address) => isSameAddress(address, preferredInvoiceToAddress) && address.invoiceToAddress) ??
        addressList.find((address) => address.invoiceToAddress)

      const invoiceResult = await updateInvoiceToAddressAction(nextInvoiceToAddress)
      setBasketData(invoiceResult.basketData)
      setSelectedInvoiceToAddress(nextInvoiceToAddress)

      const nextShipToAddress =
        addressList.find(
          (address) => address.shipToAddress && address.urn === effectiveBasketData?.data?.commonShipToAddress
        ) ??
        (nextInvoiceToAddress?.shipToAddress ? nextInvoiceToAddress : undefined) ??
        addressList.find((address) => address.shipToAddress && address.preferredShippingAddress === "true") ??
        addressList.find((address) => isSameAddress(address, preferredShipToAddress) && address.shipToAddress) ??
        addressList.find((address) => address.shipToAddress)

      const shipResult = await updateShipToAddressAction(nextShipToAddress)
      setBasketData(shipResult.basketData)
      setSelectedShipToAddress(nextShipToAddress)
    },
    [basketData, preferredInvoiceToAddress, preferredShipToAddress, setBasketData]
  )

  /*Initial setup of selected address if basket and addresses are loaded*/
  useEffect(() => {
    if (initial && basketData && addresses && addresses.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      syncCheckoutAddresses(addresses).then(() => {
        setInitial(false)
      })
    }
  }, [addresses, basketData, initial, syncCheckoutAddresses])

  /*Basket Update Invoice To Address*/
  useEffect(() => {
    if (updateInvoiceAddress) {
      updateInvoiceToAddressAction(selectedInvoiceToAddress).then((result) => {
        setBasketData(result.basketData)
      })
      // console.dir(selectedInvoiceToAddress)
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUpdateInvoiceAddress(false)
    }
  }, [selectedInvoiceToAddress, updateInvoiceAddress, setBasketData])

  /*Basket Update Ship To Address*/
  useEffect(() => {
    if (updateShipToAddress) {
      updateShipToAddressAction(selectedShipToAddress).then((result) => {
        setBasketData(result.basketData)
      })
      // console.dir(selectedShipToAddress)
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUpdateShipToAddress(false)
    }
  }, [selectedShipToAddress, updateShipToAddress, setBasketData])

  function changeSelectedInvoiceToAddress(address: ExtendedAddressRO) {
    // console.dir(address)
    setUpdateInvoiceAddress(true) //set to true to also update basket
    setSelectedInvoiceToAddress(address)
  }

  function changeSelectedShipToAddress(address: ExtendedAddressRO) {
    // console.dir(address)
    setUpdateShipToAddress(true) //set to true to also update basket
    setSelectedShipToAddress(address)
  }

  async function refreshAddressesAfterEdit() {
    const customerNo = checkoutContext?.customerData?.customerNo

    if (!customerNo) {
      return addresses ?? []
    }

    const newAddresses = await fetchAddressesForCustomerAction(customerNo)
    setAddresses(newAddresses)

    return newAddresses
  }

  function findRefreshedAddress(addressList: ExtendedAddressRO[], address: ExtendedAddressRO) {
    return addressList.find((refreshedAddress) => isSameAddress(refreshedAddress, address))
  }

  async function handleEditInvoiceToAddress(address: ExtendedAddressRO) {
    // console.dir(address)
    const refreshedAddresses = await refreshAddressesAfterEdit()
    await syncCheckoutAddresses(refreshedAddresses)
  }

  async function handleEditShipToAddress(address: ExtendedAddressRO) {
    // console.dir(address)
    const refreshedAddresses = await refreshAddressesAfterEdit()
    await syncCheckoutAddresses(refreshedAddresses)
  }

  const countryCodeUppercase = getCountry(checkoutContext.isoLocale)?.toUpperCase() ?? ""
  const country = countryForCountryCodeMap[countryCodeUppercase]
  const initialAddress = getInitialAddress(
    checkoutContext.userData,
    checkoutContext.customerData,
    countryCodeUppercase,
    t(`country.${country}`)
  )

  async function onSaveNewInitialAddress(newAddress: ExtendedAddressRO) {
    const customerNo = checkoutContext?.customerData?.customerNo
    if (customerNo) {
      const newAddresses = await fetchAddressesForCustomerAction(checkoutContext.customerData?.customerNo ?? "")

      setAddresses(newAddresses)

      await updateInvoiceToAddressAction(newAddresses[0])
      const result = await updateShipToAddressAction(newAddresses[0])

      if (result?.basketData) {
        setInitial(true)
        setBasketData(result.basketData)
      }
    }
  }

  async function onAddInvoiceToAddress(newAddress: ExtendedAddressRO) {
    const customerNo = checkoutContext?.customerData?.customerNo
    if (customerNo) {
      const newAddresses = await fetchAddressesForCustomerAction(checkoutContext.customerData?.customerNo ?? "")
      /*console.dir(newAddresses)*/
      setAddresses(newAddresses)

      const createdAddress =
        findRefreshedAddress(newAddresses, newAddress) ?? newAddresses.find((address) => address.invoiceToAddress)

      if (createdAddress) {
        const result = await updateInvoiceToAddressAction(createdAddress)
        setSelectedInvoiceToAddress(createdAddress)
        setBasketData(result.basketData)
        await syncCheckoutAddresses(newAddresses, result.basketData)
      }
    }
  }

  async function onAddShipToAddress(newAddress: ExtendedAddressRO) {
    const customerNo = checkoutContext?.customerData?.customerNo
    if (customerNo) {
      const newAddresses = await fetchAddressesForCustomerAction(checkoutContext.customerData?.customerNo ?? "")
      /*
      console.dir(newAddresses)
*/
      setAddresses(newAddresses)

      const createdAddress =
        findRefreshedAddress(newAddresses, newAddress) ?? newAddresses.find((address) => address.shipToAddress)

      if (createdAddress) {
        const result = await updateShipToAddressAction(createdAddress)
        setSelectedShipToAddress(createdAddress)
        setBasketData(result.basketData)
        await syncCheckoutAddresses(newAddresses, result.basketData)
      }
    }
  }

  return (
    <>
      {addresses && addresses.length > 0 ? (
        <Suspense>
          {basketData && !initial ? (
            <div className={"flex w-full flex-col gap-10"}>
              <div className={"flex flex-col gap-2"}>
                <div className={"flex w-full flex-row flex-wrap items-center justify-between gap-2"}>
                  <h2 className={"text-lg font-bold"}>{blok.title_invoice_to_address}</h2>
                </div>

                <div className={"grid grid-cols-12 gap-2"}>
                  <div className={"col-span-12 md:col-span-8"}>
                    {selectedInvoiceToAddress && (basketInvoiceToAddress || preferredInvoiceToAddress) ? (
                      <SelectAddress
                        selectedAddress={selectedInvoiceToAddress}
                        initialAddress={basketInvoiceToAddress ?? preferredInvoiceToAddress}
                        addresses={invoiceToAddressesSorted}
                        onEdit={handleEditInvoiceToAddress}
                        customer={checkoutContext.customerData}
                        user={checkoutContext.userData}
                        isB2B={checkoutContext.isB2B}
                        type={"invoiceTo"}
                        blok={blok}
                      />
                    ) : (
                      <MissingAddressNotice>
                        Es ist keine Rechnungsadresse vorhanden. Bitte legen Sie eine neue Adresse an.
                      </MissingAddressNotice>
                    )}
                  </div>
                  <div className={"col-span-12 flex flex-col gap-y-2 md:col-span-3 md:col-start-10"}>
                    <AddAddressButton
                      customer={checkoutContext.customerData}
                      user={checkoutContext.userData}
                      onAdd={onAddInvoiceToAddress}
                      type={"invoiceTo"}
                      addButtonLabel={"Neue Adresse hinzufügen"}
                    />
                    <AddressSelectSheet
                      addresses={invoiceToAddressesSorted}
                      selectedAddress={selectedInvoiceToAddress}
                      onSelect={changeSelectedInvoiceToAddress}
                      isB2B={checkoutContext.isB2B}
                    />
                  </div>
                </div>
              </div>
              <div className={"flex flex-col gap-2"}>
                <div className={"flex w-full flex-row flex-wrap items-baseline justify-start gap-2"}>
                  <h2 className={"text-lg font-bold"}>{blok.title_ship_to_address} </h2>
                  <h2 className={"text-md font-normal"}>
                    {isShipToSameAsInvoiceTo ? "(" + blok.title_ship_to_address_same_as_invoice + ")" : ""}
                  </h2>
                </div>
                <div className={"grid grid-cols-12 gap-2"}>
                  <div className={"col-span-12 md:col-span-8"}>
                    {!isShipToSameAsInvoiceTo &&
                      (selectedShipToAddress && (basketShipToAddress || preferredShipToAddress) ? (
                        <SelectAddress
                          selectedAddress={selectedShipToAddress}
                          initialAddress={basketShipToAddress ?? preferredShipToAddress}
                          addresses={shipToAddressesSorted}
                          onEdit={handleEditShipToAddress}
                          customer={checkoutContext.customerData}
                          user={checkoutContext.userData}
                          isB2B={checkoutContext.isB2B}
                          type={"shipTo"}
                          blok={blok}
                        />
                      ) : (
                        <MissingAddressNotice>
                          Es ist keine Lieferadresse vorhanden. Bitte legen Sie eine neue Adresse an.
                        </MissingAddressNotice>
                      ))}
                  </div>
                  <div className={"col-span-12 flex flex-col gap-y-2 md:col-span-3 md:col-start-10"}>
                    <AddAddressButton
                      customer={checkoutContext.customerData}
                      user={checkoutContext.userData}
                      onAdd={onAddShipToAddress}
                      type={"shipTo"}
                      addButtonLabel={"Neue Adresse hinzufügen"}
                    />
                    <AddressSelectSheet
                      addresses={shipToAddressesSorted}
                      selectedAddress={selectedShipToAddress}
                      onSelect={changeSelectedShipToAddress}
                      isB2B={checkoutContext.isB2B}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={"flex w-full flex-col gap-8"}>
              <FallbackCard className={"w-full"} />
              <FallbackCard className={"w-full"} />
            </div>
          )}
        </Suspense>
      ) : (
        <>
          <Suspense>
            {basketData && addresses ? (
              <div className={"flex w-full flex-col gap-10"}>
                <div className={"flex flex-col gap-2"}>
                  <div className={"flex w-full flex-row flex-wrap items-center justify-between gap-2"}>
                    <h2 className={"text-lg font-bold"}>{blok.title_initial_address}</h2>
                  </div>

                  <div className={"grid grid-cols-12 gap-2"}>
                    <div className={"col-span-12"}>
                      <EditOrCreateAddress
                        address={initialAddress}
                        customer={checkoutContext.customerData}
                        user={checkoutContext.userData}
                        onSave={onSaveNewInitialAddress}
                        mode={"initial"}
                        type={"invoiceTo"}
                        validateOnFirstRender={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className={"flex w-full flex-col gap-8"}>
                <FallbackCard className={"h-40 w-full"} />
              </div>
            )}
          </Suspense>
        </>
      )}
    </>
  )
}
